import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { stopClarityForInternalUser } from "@/lib/clarity";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  isPropertyManager: boolean;
  isOwner: boolean;
  canAccessAdmin: boolean;
  canAccessOwnerPortal: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const PASSWORD_RECOVERY_STORAGE_KEY = "amplify-password-recovery";

const markPasswordRecoveryUrl = () => {
  if (window.location.pathname !== "/reset-password") return;

  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hashParams = new URLSearchParams(hash);
  const searchParams = new URLSearchParams(window.location.search);
  const isRecoveryUrl =
    hashParams.get("type") === "recovery" ||
    searchParams.get("type") === "recovery" ||
    hashParams.has("access_token") ||
    searchParams.has("token_hash") ||
    searchParams.has("code");

  if (isRecoveryUrl) {
    window.sessionStorage.setItem(PASSWORD_RECOVERY_STORAGE_KEY, "true");
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPropertyManager, setIsPropertyManager] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkRoles = async (userId: string) => {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    if (error) console.error("checkRoles error", error);
    const roles = (data ?? []).map((r: { role: string }) => r.role);
    setIsAdmin(roles.includes("admin"));
    setIsPropertyManager(roles.includes("property_manager"));
    setIsOwner(roles.includes("owner"));
    // Internal people should never appear in guest behaviour analytics.
    if (roles.some((r) => r === "admin" || r === "property_manager" || r === "owner")) {
      stopClarityForInternalUser();
    }
  };


  useEffect(() => {
    let initialized = false;
    markPasswordRecoveryUrl();

    const handleSession = async (session: Session | null) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        setLoading(true);
        try {
          await checkRoles(session.user.id);
        } finally {
          setLoading(false);
        }
      } else {
        setIsAdmin(false);
        setIsPropertyManager(false);
        setIsOwner(false);
        setLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "PASSWORD_RECOVERY") {
          window.sessionStorage.setItem(PASSWORD_RECOVERY_STORAGE_KEY, "true");
        }
        if (!initialized) return;
        setTimeout(() => { handleSession(session); }, 0);
      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      await handleSession(session);
      initialized = true;
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const canAccessAdmin = isAdmin || isPropertyManager;
  const canAccessOwnerPortal = isAdmin || isOwner;

  return (
    <AuthContext.Provider value={{ session, user, isAdmin, isPropertyManager, isOwner, canAccessAdmin, canAccessOwnerPortal, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
